import {
  isInvoicingSessionCancelled,
  isNonInvoicedInvoicingSessionCancelledByPatient,
  isNonInvoicedInvoicingSessionCancelledByPro,
} from "components/Dashboard/DoctorDashboard/utils/isInvoicingSessionCancelled";
import RecurringTag from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-sidebar/components/session-sidesheet/session-details/recurring-tag/RecurringTag";
import SidesheetHeader from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-sidebar/components/sidesheet-header/SidesheetHeader";
import { SUPPORT_GOING_BACK_FROM_SESSION_REVIEW } from "constants/featureFlags";
import useLocationParams from "hooks/useLocationParams";
import messages from "messages";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  closeSidesheet,
  selectAppointmentsWithInvoiceCreationInProgress,
  selectFocusedSessionNextStatus,
  showEditSession,
} from "store/calendarSidebarSlice";
import { PRO_CALENDAR_ANALYTICS_PAGE_NAME, track } from "utils/amplitude";
import canCancelSession from "utils/canCancelSession";
import canJoinSessionForPro from "utils/canJoinSessionForPro";
import getClasses from "utils/getClasses";
import SidesheetWarning from "../../sidesheet-warning/SidesheetWarning";
import WaitlistClient from "../../waitlist-client/WaitlistClient";
import styles from "../SessionSidesheet.module.scss";
import AdminNoteSessionDetilsSection from "./admin-note-session-details-section/AdminNoteSessionDetilsSection";
import EditSeriesModal from "./edit-series-modal/EditSeriesModal";
import DeleteSeriesModal, {
  extractZoomMeetingId,
} from "./delete-series-modal/DeleteSeriesModal";
import LocationSessionDetilsSection from "./location-session-details-section/LocationSessionDetilsSection";
import MainSessionDetailsSection from "./main-session-details-section/MainSessionDetailsSection";
import NotesSessionDetailsSection from "./notes-session-details-section/NotesSessionDetailsSection";
import PaymentSessionDetailsSection from "./payment-session-details-section/PaymentSessionDetailsSection";
import SessionDetailsFooter from "./session-details-footer/SessionDetailsFooter";
import deleteInvoicingAppointment from "services/scheduler/deleteInvoicingAppointment";
import ConfirmModal from "components/confirm-modal";
import deleteZoomMeeting from "services/zoom/deleteZoomMeeting";

type AppointmentPropType = AppointmentWithSeriesInfo & {
  invoice?: InvoiceData;
};

interface SessionDetailsProps {
  appointment: AppointmentPropType;
}

const AMPLITUDE_COMPONENT_NAME = "session-info-pane";

const SessionDetails = ({ appointment }: SessionDetailsProps) => {
  const dispatch = useDispatch();
  const nextStatus = useSelector(selectFocusedSessionNextStatus);
  const { setParam, deleteParam } = useLocationParams();

  const isCancelled = appointment.statusFlags.cancelledOrAbandoned;

  const nowValue = Date.now();

  const [showEditSeriesModal, setShowEditSeriesModal] = useState(false);
  const [showDeleteSeriesModal, setShowDeleteSeriesModal] = useState(false);
  const [showConfirmDeleteSession, setShowConfirmDeleteSession] =
    useState(false);
  const [showInvoiceWarningModal, setShowInvoiceWarningModal] = useState(false);

  const [error, setError] = useState<any>();
  const [busy, setBusy] = useState(false);

  const appointmentsWithInvoiceCreationInProgress = useSelector(
    selectAppointmentsWithInvoiceCreationInProgress,
  );
  const isWaitingForInvoice = useMemo(
    () =>
      appointmentsWithInvoiceCreationInProgress.includes(
        appointment.appointment_id,
      ),
    [appointmentsWithInvoiceCreationInProgress, appointment.appointment_id],
  );

  const { startValue, diffHrs, endValue } = useMemo(() => {
    const startValue = new Date(appointment.appointment_start).valueOf();
    const endValue = new Date(appointment.appointment_end).valueOf();
    const diffHrs = (endValue - startValue) / 1000 / 60 / 60;
    return { startValue, diffHrs, endValue };
  }, [appointment]);

  const isActive = useMemo(
    () => startValue <= nowValue && nowValue <= endValue,
    [startValue, endValue, nowValue],
  );

  const isInThePast = useMemo(() => endValue < nowValue, [endValue, nowValue]);

  const canSessionBeJoinedAtCurrentTime = useMemo(
    () => canJoinSessionForPro(startValue, endValue),
    [startValue, endValue],
  );

  const canCancel = useMemo(() => canCancelSession(endValue), [endValue]);

  const canEdit = useMemo(() => {
    if (appointment.statusFlags.usingInvoicing) {
      return !isInvoicingSessionCancelled(appointment, nextStatus);
    }
    return (
      !canSessionBeJoinedAtCurrentTime && !isCancelled
      // &&
      // !appointment.appointment_series?.repeat_rule
    );
  }, [appointment, canSessionBeJoinedAtCurrentTime, isCancelled, nextStatus]);

  const isPartOfASeries = useMemo(
    () =>
      !!(
        appointment.appointment_series_id &&
        appointment.appointment_series?.repeat_rule
      ),
    [appointment],
  );
  const shouldShowViewReceiptLink = useMemo(
    () =>
      !canSessionBeJoinedAtCurrentTime &&
      !canCancel &&
      appointment.invoice_available === "available",
    [appointment, canCancel, canSessionBeJoinedAtCurrentTime],
  );

  const invoiceSessionStatus = useMemo(() => {
    if (appointment.invoice) {
      return appointment.invoice.appointment_status;
    }

    if (isNonInvoicedInvoicingSessionCancelledByPro(appointment)) {
      return "cancel";
    }

    if (isNonInvoicedInvoicingSessionCancelledByPatient(appointment)) {
      return "cancel_by_patient";
    }

    return "complete";
  }, [appointment]);

  const feeCharged = useMemo(() => {
    switch (appointment.cancellation_fee_override) {
      case "full_fee":
        return "Full Session Fee";
      case "no_charge":
        return "Fee Waiver";
      case "charge_late_cancellation_fee":
        return "Cancellation Fee";
      default:
        return "";
    }
  }, [appointment.cancellation_fee_override]);

  useEffect(() => {
    setError("");

    if (!SUPPORT_GOING_BACK_FROM_SESSION_REVIEW) {
      return;
    }

    setParam("session_id", appointment.appointment_id.toString());
    return () => deleteParam("session_id");
  }, [appointment.appointment_id, setParam, deleteParam]);

  const handleEditSession = useCallback(() => {
    if (appointment.appointment_series?.repeat_rule) {
      setShowEditSeriesModal(true);
      return;
    }
    dispatch(showEditSession({ appointment, editSeriesMode: "this_session" }));
  }, [dispatch, appointment]);

  const handleDeleteSession = useCallback(async () => {
    if (appointment.invoice) {
      setShowInvoiceWarningModal(true);
      return;
    }

    if (appointment.appointment_series?.repeat_rule) {
      setShowDeleteSeriesModal(true);
      return;
    } else {
      setShowConfirmDeleteSession(true);
    }
  }, [appointment]);

  const onConfirmDeleteSession = useCallback(async () => {
    try {
      await deleteInvoicingAppointment(appointment.appointment_id);
      setShowConfirmDeleteSession(false);
      dispatch(closeSidesheet());
    } catch (error) {
      setError(error);
    }

    const { external_meeting_link } = appointment;
    const zoomMeetingId = extractZoomMeetingId(external_meeting_link);

    if (zoomMeetingId) {
      try {
        await deleteZoomMeeting({ meetingId: zoomMeetingId });
      } catch (error) {
        console.error(error);
      }
    }
  }, [appointment, dispatch]);

  const trackClick = (elementName: string) =>
    track(
      PRO_CALENDAR_ANALYTICS_PAGE_NAME,
      [AMPLITUDE_COMPONENT_NAME, elementName],
      "click",
    );

  const typeIndicatorClassName = useMemo(
    () =>
      getClasses(
        styles["type-indicator"],
        !isCancelled &&
          appointment.statusFlags.unconfirmedInvite &&
          styles["type-indicator--invited"],
        !isCancelled && isActive && styles["type-indicator--active"],
        isCancelled && styles["type-indicator--cancelled"],
      ),
    [appointment, isCancelled, isActive],
  );

  return (
    <>
      <SidesheetHeader
        title={messages.sessionDetails}
        onDelete={
          appointment.statusFlags.usingInvoicing
            ? handleDeleteSession
            : undefined
        }
        onEdit={canEdit ? handleEditSession : undefined}
        onClose={() => {
          trackClick("close-button");
          dispatch(closeSidesheet());
        }}
        typeIndicatorClassName={typeIndicatorClassName}
      />

      {appointment.patient?.client_status === "waitlist" && (
        <WaitlistClient message={"Waitlist Client"} />
      )}

      {appointment.statusFlags.unconfirmedInvite && (
        <SidesheetWarning
          message={messages.pendingPaymentConfirmationWarning}
        />
      )}

      {error && <SidesheetWarning message={error.toString()} />}

      <div className={styles["body"]}>
        <MainSessionDetailsSection
          appointment={appointment}
          start={startValue}
          end={endValue}
          diffHrs={diffHrs}
          setError={setError}
          trackClick={trackClick}
          isWaitingForInvoice={isWaitingForInvoice}
          invoiceSessionStatus={invoiceSessionStatus}
        />

        {isPartOfASeries && (
          <div className={styles["section"]}>
            <div className={styles["section__title"]}>{messages.repeat}</div>
            <RecurringTag
              repeatRule={appointment.appointment_series!.repeat_rule}
            />
          </div>
        )}

        <LocationSessionDetilsSection
          appointment={appointment}
          trackClick={trackClick}
        />

        <NotesSessionDetailsSection
          appointment={appointment}
          trackClick={trackClick}
        />

        <PaymentSessionDetailsSection
          appointment={appointment}
          isInThePast={isInThePast}
          isWaitingForInvoice={isWaitingForInvoice}
          trackClick={trackClick}
          isCancelled={isCancelled}
          invoiceSessionStatus={invoiceSessionStatus}
          feeCharged={feeCharged}
          setError={setError}
        />

        {appointment.admin_note && (
          <AdminNoteSessionDetilsSection adminNote={appointment.admin_note} />
        )}
      </div>

      <SessionDetailsFooter
        appointment={appointment}
        nextStatus={nextStatus}
        isPartOfASeries={isPartOfASeries}
        busy={busy}
        setBusy={setBusy}
        setError={setError}
        canCancel={canCancel}
        isCancelled={isCancelled}
        shouldShowViewReceiptLink={shouldShowViewReceiptLink}
        canSessionBeJoinedAtCurrentTime={canSessionBeJoinedAtCurrentTime}
        trackClick={trackClick}
      />

      {showEditSeriesModal && (
        <EditSeriesModal
          show={showEditSeriesModal}
          onHide={() => setShowEditSeriesModal(false)}
          onConfirm={(editSeriesMode) => {
            setShowEditSeriesModal(false);
            dispatch(showEditSession({ appointment, editSeriesMode }));
          }}
        />
      )}

      {showDeleteSeriesModal && (
        <DeleteSeriesModal
          show={showDeleteSeriesModal}
          onHide={() => setShowDeleteSeriesModal(false)}
          onConfirm={() => {
            setShowDeleteSeriesModal(false);
            dispatch(closeSidesheet());
          }}
          appointment={appointment}
        />
      )}

      {showConfirmDeleteSession && (
        <ConfirmModal
          show={showConfirmDeleteSession}
          onHide={() => setShowConfirmDeleteSession(false)}
          onCancel={() => setShowConfirmDeleteSession(false)}
          onConfirm={() => {
            onConfirmDeleteSession();
          }}
          labelTitle={"Delete this session?"}
          children={
            "This will delete the session and all of its data, including progress note and associated invoices. This action cannot be undone."
          }
          labelCancel={"Cancel"}
          labelConfirm={"Yes, delete"}
          footerClass={styles["confirm-delete__footer"]}
        />
      )}

      {showInvoiceWarningModal && (
        <ConfirmModal
          show={showInvoiceWarningModal}
          onHide={() => setShowInvoiceWarningModal(false)}
          onCancel={() => setShowInvoiceWarningModal(false)}
          onConfirm={() => setShowInvoiceWarningModal(false)}
          labelTitle={"Unable to Delete Appointment"}
          children={
            "This appointment is linked to an invoice. Delete the invoice first, then you'll be able to remove the appointment."
          }
          hideCancel
          labelConfirm={"Got It"}
        />
      )}
    </>
  );
};

export default SessionDetails;
